import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, axios } from "@/utils/constants";
import { Pencil, FilePlus2, FileText, Loader2, Search, Car, Trash2, Zap, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { formatReferenceNumber } from "@/utils/afsol";
import { useAuth } from "@/context/AuthContext";
import { NewAfsolWizard } from "@/components/afsol/NewAfsolWizard";

const formatLoanNumber = (loanNumber) => {
  if (!loanNumber) return "Ónúmerað lán";
  return `Láns nr. ${String(loanNumber).padStart(4, "0")}`;
};

import { fmtDateIS } from "../utils/formatDate";

const formatDate = (value) => {
  if (!value) return "—";
  return fmtDateIS(value);
};

export const AfsolPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [afsals, setAfsals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [editAfsalId, setEditAfsalId] = useState(null);
  const createdBy = user?.full_name && user.full_name !== "FONDO Administrator" ? user.full_name : "Javier Enrique";

  const fetchAfsals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/afsals`);
      setAfsals(response.data);
    } catch (error) {
      toast.error("Ekki tókst að sækja afsöl");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAfsal = async (event, afsalId) => {
    event.stopPropagation();
    if (!window.confirm("Ertu viss um að þú viljir eyða þessu afsali?")) return;
    try {
      await axios.delete(`${API}/afsals/${afsalId}`);
      toast.success("Afsali eytt");
      fetchAfsals();
    } catch {
      toast.error("Ekki tókst að eyða afsali");
    }
  };

  useEffect(() => {
    fetchAfsals();
  }, []);

  const summary = useMemo(() => {
    const draftCount = afsals.filter((item) => item.status === "draft").length;
    return {
      total: afsals.length,
      draftCount,
      latestDate: afsals[0]?.created_at ? formatDate(afsals[0].created_at) : "—",
    };
  }, [afsals]);

  const filteredAfsals = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return afsals.filter((afsal) => {
      const statusMatch = statusFilter === "all" ? true : afsal.status === statusFilter;
      const textMatch = !query
        ? true
        : [
            afsal.registration_number,
            afsal.seller_name,
            afsal.buyer_name,
            afsal.buyer_kennitala,
            afsal.vehicle_type,
            afsal.reference_number ? String(afsal.reference_number) : "",
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));

      return statusMatch && textMatch;
    });
  }, [afsals, searchQuery, statusFilter]);

  const openPrintPreview = (afsalId, mode) => {
    window.open(`/afsol/${afsalId}/print/${mode}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-8" data-testid="afsol-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="afsol-page-title">Afsöl</h1>
          <div className="flex gap-2">
            <Button onClick={() => fetchAfsals()} className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2" data-testid="refresh-afsol-button">
              Uppfæra
            </Button>
            <Button onClick={() => navigate("/afsol/new")} variant="outline" className="h-6 text-[10px] px-2" data-testid="new-afsol-advanced-button">
              <Settings2 className="h-3 w-3 mr-1" /> Ítarlegt form
            </Button>
            <Button onClick={() => setShowWizard(true)} className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2" data-testid="new-afsol-button">
              Ný afsal
            </Button>
          </div>
        </div>

        <div className="mb-4 flex gap-4 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="afsol-status-filter">
              <SelectValue placeholder="Sía eftir stöðu" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Öll afsöl</SelectItem>
              <SelectItem value="draft">Drög</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Leita eftir bílnr./nafni..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-10"
              data-testid="afsol-search-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-4 mb-3 text-[10px] text-gray-500" data-testid="afsol-summary-counts">
          <span className="text-green-600">Drög <span className="font-semibold">{summary.draftCount}</span></span>
          <span className="text-blue-600">Samtals <span className="font-semibold">{summary.total}</span></span>
          <span className="text-gray-400">Síðast stofnað <span className="font-semibold">{summary.latestDate}</span></span>
        </div>

        {loading ? (
          <div className="py-12 text-sm text-gray-500 flex items-center gap-2" data-testid="afsol-loading-state">
            <Loader2 className="h-4 w-4 animate-spin" />
            Hleð afsölum...
          </div>
        ) : filteredAfsals.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-14 text-center" data-testid="afsol-empty-state">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Car className="h-5 w-5 text-gray-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Engin afsöl fundust</h2>
            <p className="mt-2 text-sm text-gray-500">Prófaðu að breyta leit eða stofna nýtt afsal.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5" data-testid="afsol-list">
            {filteredAfsals.map((afsal) => (
              <div
                key={afsal.id}
                onClick={() => navigate(`/afsol/${afsal.id}`)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 flex items-center gap-3 cursor-pointer hover:border-blue-200 hover:bg-blue-50/30"
                data-testid={`afsol-card-${afsal.id}`}
              >
                <div className="shrink-0 w-14">
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-[10px] px-2 py-0.5" data-testid={`afsol-card-status-${afsal.id}`}>
                    Drög
                  </Badge>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs truncate" data-testid={`afsol-card-registration-${afsal.id}`}>
                      {afsal.registration_number || "Óskráð bílnúmer"}
                    </span>
                    <span className="text-[9px] text-gray-400 bg-gray-100 rounded px-1 py-0.5 shrink-0">
                      {afsal.vehicle_type || "Afsal"}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400 truncate" data-testid={`afsol-card-seller-${afsal.id}`}>
                    {afsal.seller_name || "Óskráður seljandi"} → {afsal.buyer_name || "Óskráður kaupandi"}
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-[10px] font-mono text-gray-500 w-[7.5rem] text-left tabular-nums" data-testid={`afsol-card-reference-number-${afsal.id}`}>
                    {formatReferenceNumber(afsal.reference_number)}
                  </span>
                  <span className="text-[10px] text-gray-400 w-[6.5rem] text-right tabular-nums" data-testid={`afsol-card-date-${afsal.id}`}>
                    {formatDate(afsal.afsal_date)}
                  </span>
                  <span className="text-[10px] text-gray-400 w-[7rem] text-right tabular-nums" data-testid={`afsol-card-created-at-${afsal.id}`}>
                    {formatDate(afsal.created_at)}
                  </span>
                </div>

                <div className="flex items-center gap-0.5 shrink-0 border-l pl-2">
                  {afsal.loan_number ? (
                    <span className="text-[10px] text-gray-400 mr-2" data-testid={`afsol-card-loan-number-${afsal.id}`}>
                      {formatLoanNumber(afsal.loan_number)}
                    </span>
                  ) : null}
                  <Button
                    variant="ghost"
                    onClick={(event) => {
                      event.stopPropagation();
                      openPrintPreview(afsal.id, "handwritten");
                    }}
                    className="p-1 h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Prenta handundirritun"
                    data-testid={`print-afsol-${afsal.id}`}
                  >
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={(event) => {
                      event.stopPropagation();
                      openPrintPreview(afsal.id, "electronic");
                    }}
                    className="p-1 h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50"
                    title="PDF fyrir rafræna undirritun"
                    data-testid={`electronic-afsol-${afsal.id}`}
                  >
                    <div className="relative">
                      <FileText className="h-3.5 w-3.5" />
                      <Zap className="h-2.5 w-2.5 absolute -top-1 -right-1" />
                    </div>
                  </Button>
                  <Button variant="ghost" onClick={(event) => {
                    event.stopPropagation();
                    setEditAfsalId(afsal.id);
                    setShowWizard(true);
                  }} className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Breyta" data-testid={`edit-afsol-${afsal.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" onClick={(event) => handleDeleteAfsal(event, afsal.id)} className="p-1 h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50" title="Eyða afsali" data-testid={`delete-afsol-${afsal.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <NewAfsolWizard
        open={showWizard}
        onOpenChange={(open) => { setShowWizard(open); if (!open) setEditAfsalId(null); }}
        createdBy={createdBy}
        onCreated={() => fetchAfsals()}
        editAfsalId={editAfsalId}
      />
    </div>
  );
};

export default AfsolPage;